import type { FeeProfile, MarketplaceCode } from '../engine/types';
import { assertValidProfile } from './schema';

const cache = new Map<string, Map<MarketplaceCode, FeeProfile>>();

export const MARKET_ORDER: MarketplaceCode[] = ['US', 'CA', 'UK', 'DE'];

export function isMetricMarketplace(market: MarketplaceCode): boolean {
  return market === 'CA' || market === 'UK' || market === 'DE';
}

function normalizeBaseUrl(baseUrl?: string): string {
  const raw = baseUrl && baseUrl.trim().length ? baseUrl.trim() : '/';
  return raw.endsWith('/') ? raw : `${raw}/`;
}

function buildProfileUrl(baseUrl: string, market: MarketplaceCode): string {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const relative = `fee-profiles/${market.toLowerCase()}.json`;

  try {
    return new URL(relative, normalizedBaseUrl).toString();
  } catch {
    return `${normalizedBaseUrl}${relative}`;
  }
}

function getCacheForBase(baseUrl: string): Map<MarketplaceCode, FeeProfile> {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  let entry = cache.get(normalizedBaseUrl);

  if (!entry) {
    entry = new Map<MarketplaceCode, FeeProfile>();
    cache.set(normalizedBaseUrl, entry);
  }

  return entry;
}

export async function loadProfile(market: MarketplaceCode, baseUrl?: string): Promise<FeeProfile> {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const scopedCache = getCacheForBase(normalizedBaseUrl);

  if (scopedCache.has(market)) {
    return scopedCache.get(market) as FeeProfile;
  }

  const profileUrl = buildProfileUrl(normalizedBaseUrl, market);
  const response = await fetch(profileUrl, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load fee profile for ${market} from ${profileUrl}`);
  }

  const raw = await response.json();
  const profile = assertValidProfile(raw);
  scopedCache.set(market, profile);
  return profile;
}

export async function loadAllProfiles(baseUrl?: string): Promise<Map<MarketplaceCode, FeeProfile>> {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const result = new Map<MarketplaceCode, FeeProfile>();
  for (const code of MARKET_ORDER) {
    result.set(code, await loadProfile(code, normalizedBaseUrl));
  }
  return result;
}
